<script setup lang="ts">
import { computed, unref } from 'vue'
import { VIcon } from '@/modules/shared/components'
import { isMultiFilter, isRangeFilter } from './utils'
import type { FilterConfig } from './types'

const props = defineProps<{ filters: FilterConfig[] }>()
const emit = defineEmits<{ remove: [filterKey: string] }>()

type Badge = {
  id: string
  label: string
  remove: () => void
}

const activeBadges = computed<Badge[]>(() => {
  const badges: Badge[] = []

  for (const filter of props.filters) {
    if (isMultiFilter(filter)) {
      const options = unref(filter.options) ?? []
      const selected = filter.model.value

      if (!selected.length || selected.length === options.length) continue

      for (const value of selected) {
        const option = options.find((o) => o.value === value)
        const optionLabel = option?.label ?? option?.text ?? value

        badges.push({
          id: `${filter.key}:${value}`,
          label: `${filter.label}: ${optionLabel}`,
          remove: () => {
            filter.model.value = filter.model.value.filter((v) => v !== value)
            emit('remove', filter.key)
          },
        })
      }
    } else if (isRangeFilter(filter)) {
      const start = filter.range.start.value
      const end = filter.range.end.value

      if (!start && !end) continue

      let rangeLabel = filter.label + ': '
      if (start && end) rangeLabel += `${start} → ${end}`
      else if (start) rangeLabel += `From ${start}`
      else rangeLabel += `Through ${end}`

      badges.push({
        id: `${filter.key}:range`,
        label: rangeLabel,
        remove: () => {
          filter.range.start.value = null
          filter.range.end.value = null
          emit('remove', filter.key)
        },
      })
    }
  }

  return badges
})
</script>

<template>
  <div v-if="activeBadges.length" class="flex flex-wrap gap-2">
    <span
      v-for="badge in activeBadges"
      :key="badge.id"
      class="inline-flex cursor-default items-center gap-1 rounded-full bg-[#3b88af]/10 px-2.5 py-0.5 text-sm text-[#3b88af]"
    >
      <span>{{ badge.label }}</span>
      <button
        type="button"
        class="ml-0.5 inline-flex items-center rounded-full p-0.5 hover:bg-[#3b88af]/20"
        aria-label="Remove filter"
        @click="badge.remove()"
      >
        <VIcon name="X" class="h-3 w-3" />
      </button>
    </span>
  </div>
</template>
